/**
 * Governance → Desktop mascot gallery → "Character reference" (components/CharacterReferenceSection.tsx; owner,
 * 2026-10-31: the approved identity reference of Gyro is hosted by gyro-svc, seeded from the files it ships, and an IT
 * admin replaces a slot here so a new approved master never needs a release). This file holds the view's types, the
 * slots' wording, the limits mirrored from services/gyro-svc/src/mascotReference.ts (cross-tested against them), a
 * check of a chosen file's PNG header before anything is sent, and the bodies the section sends.
 */

export const REFERENCE_SLOTS = ['left-master', 'left-prompt', 'character-card'] as const;
export type ReferenceSlot = (typeof REFERENCE_SLOTS)[number];

export const SLOT_LABELS: Readonly<Record<ReferenceSlot, { title: string; hint: string }>> = {
  'left-master': { title: 'Left-facing master', hint: 'The approved master Gyro places in its code sandbox on a render ask. 8-bit RGBA PNG, 512 to 2,048 px a side.' },
  'left-prompt': { title: 'Prompt reference', hint: 'The image every render ask carries to the model. The desktop mascot rules apply: 8-bit RGBA PNG, 256 to 1,024 px, at most 768,000 bytes, a real cut-out.' },
  'character-card': { title: 'Character card', hint: 'The character package\'s card, for people. 8-bit RGBA PNG, 512 to 2,048 px a side.' },
};

/** mascotReference.ts MASCOT_REFERENCE_LIMITS, slot by slot. */
export const REFERENCE_LIMITS: Readonly<Record<ReferenceSlot, { maxBytes: number; maxSide: number; minLongSide: number; maxAspect: number }>> = {
  'left-master': { maxBytes: 2_500_000, maxSide: 2048, minLongSide: 512, maxAspect: 2 },
  'left-prompt': { maxBytes: 1_000_000, maxSide: 1024, minLongSide: 256, maxAspect: 2 },
  'character-card': { maxBytes: 3_500_000, maxSide: 2048, minLongSide: 512, maxAspect: 2 },
};

export interface ReferenceRecord {
  sha256: string;
  sizeBytes: number;
  width: number;
  height: number;
  source: 'seed' | 'admin';
  setAt: string;
  setBy?: string;
}
export interface ReferenceSlotView {
  record: (ReferenceRecord & { previous?: ReferenceRecord }) | null;
  seed: { file: string; sha256: string; label: string };
  isSeed: boolean;
}
export interface ReferenceView {
  schemaVersion: 1;
  revision: number;
  slots: Record<ReferenceSlot, ReferenceSlotView>;
  /** On a change's answer: whether anything changed (the same image again changes nothing). */
  changed?: boolean;
  /** On a change's answer: the change was made but the audit ledger refused its record. */
  auditMissed?: boolean;
}

export const REFERENCE_HINT = 'The identity reference Gyro supplies with every ask for a picture, outfit, animation or video of Gyro (and places in its code sandbox). Seeded from the files the service ships; replace a slot here when the owner approves a new master, and roll it back one step if needed.';
export const REFERENCE_UNREADABLE_TEXT = 'Gyro could not read the character reference just now. Render asks use the shipped files meanwhile. Reload in a minute.';
export const REFERENCE_CHANGED_TEXT = 'Another admin changed the character reference after you loaded it, so nothing was changed. It has been reloaded; try again.';
export const REFERENCE_AUDIT_MISSED_TEXT = 'Changed, but the audit ledger refused its record just now and it was not written. Tell whoever keeps the audit trail.';

export function replacedText(slot: ReferenceSlot): string {
  return `${SLOT_LABELS[slot].title} replaced. Render asks use it from now on; the previous image is kept for one rollback.`;
}
export function rolledBackText(slot: ReferenceSlot): string {
  return `${SLOT_LABELS[slot].title} rolled back to its previous image.`;
}
export function unchangedText(slot: ReferenceSlot): string {
  return `${SLOT_LABELS[slot].title} already holds that image; nothing changed.`;
}

/** "d54c8e48…" for a sha256. */
export function shortSha(sha256: string): string {
  return `${sha256.slice(0, 12)}…`;
}

/** "1,254 × 1,254 px, 1,156,633 bytes". */
export function sizeText(record: Pick<ReferenceRecord, 'width' | 'height' | 'sizeBytes'>): string {
  return `${record.width.toLocaleString('en-US')} × ${record.height.toLocaleString('en-US')} px, ${record.sizeBytes.toLocaleString('en-US')} bytes`;
}

/** "The shipped seed" or "Replaced by name@exante360.com on 31 Oct 2026". */
export function sourceText(view: ReferenceSlotView): string {
  if (!view.record) return 'Not seeded yet; render asks use the shipped file.';
  if (view.isSeed) return 'The shipped seed.';
  const when = new Date(view.record.setAt);
  const date = Number.isNaN(when.getTime()) ? view.record.setAt : when.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  return `${view.record.source === 'admin' ? 'Replaced' : 'Set'}${view.record.setBy ? ` by ${view.record.setBy}` : ''} on ${date}.`;
}

/** The image URL the console shows, keyed by the bytes' sha so the browser keeps it until the slot changes. */
export function referenceImageUrl(slot: ReferenceSlot, sha256: string): string {
  return `/v1/admin/desktop/mascot/reference/${encodeURIComponent(slot)}/image?sha=${encodeURIComponent(sha256)}`;
}

const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];

/**
 * A chosen file's PNG header against the slot's rules, before anything is sent: the signature, an IHDR of 8-bit RGBA
 * (colour type 6), not interlaced, within the slot's sides and bytes. The service parses the whole image again; this
 * only saves an upload that would be refused.
 */
export function checkReferencePng(slot: ReferenceSlot, bytes: Uint8Array): { ok: true; width: number; height: number } | { ok: false; message: string } {
  const limits = REFERENCE_LIMITS[slot];
  if (bytes.length > limits.maxBytes) return { ok: false, message: `The file is ${bytes.length.toLocaleString('en-US')} bytes; this slot takes at most ${limits.maxBytes.toLocaleString('en-US')}.` };
  if (bytes.length < 33 || PNG_SIGNATURE.some((byte, index) => bytes[index] !== byte)) return { ok: false, message: 'The file is not a PNG.' };
  if (String.fromCharCode(bytes[12]!, bytes[13]!, bytes[14]!, bytes[15]!) !== 'IHDR') return { ok: false, message: 'The PNG has no header chunk where one must be.' };
  const width = ((bytes[16]! << 24) | (bytes[17]! << 16) | (bytes[18]! << 8) | bytes[19]!) >>> 0;
  const height = ((bytes[20]! << 24) | (bytes[21]! << 16) | (bytes[22]! << 8) | bytes[23]!) >>> 0;
  const depth = bytes[24]!;
  const colorType = bytes[25]!;
  const interlace = bytes[28]!;
  if (depth !== 8 || colorType !== 6) return { ok: false, message: 'The PNG must be 8-bit RGBA (colour with an alpha channel).' };
  if (interlace !== 0) return { ok: false, message: 'The PNG must not be interlaced.' };
  const longer = Math.max(width, height);
  const shorter = Math.min(width, height);
  if (shorter === 0 || longer > limits.maxSide || longer < limits.minLongSide) return { ok: false, message: `The image is ${width} × ${height} px; this slot takes ${limits.minLongSide} to ${limits.maxSide} px on the longer side.` };
  if (longer / shorter > limits.maxAspect) return { ok: false, message: `The image is ${width} × ${height} px; at most ${limits.maxAspect}:1.` };
  return { ok: true, width, height };
}

export interface ReplaceReferenceBody { pngBase64: string; expectedRevision: number }
export interface RollbackReferenceBody { expectedRevision: number }

export function replaceBody(bytes: Uint8Array, revision: number): ReplaceReferenceBody {
  let binary = '';
  for (let at = 0; at < bytes.length; at += 0x8000) binary += String.fromCharCode(...bytes.subarray(at, at + 0x8000));
  return { pngBase64: btoa(binary), expectedRevision: revision };
}
